import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import AuthContext from "../contexts/AuthContext";
import { useTheme, Avatar } from "@/design-system";
import { useToast } from "../design-system/contexts/ToastContext";
import { getEventDetailsBySponsorCode, createUserSponsorMapping, listEvents } from "../common/api";
import { AddedEventDisplayModal } from "../common/modal";
import { addAllEventsToIndexDB } from "../common/functions";
import { AppHeader } from "../components/AppLayout";
import ProfileModal from "../components/ProfileModal";
// iOS keyboard is now handled via CSS dvh units and viewport meta tag
import "../components/AppLayout/AppLayout.css";

const NoEventSponsor = () => {
  const [loading, setLoading] = useState(false);
  const [sponsorCode, setSponsorCode] = useState("");
  const [error, setError] = useState("");
  const [addedEventDatails, setAddedEventDatails] = useState(false);
  const [addedEventModal, setAddedEventModal] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const navigate = useNavigate();
  const Context = useContext(AuthContext);
  const { toggleTheme, theme } = useTheme();
  const { error: showError } = useToast();

  // Get user info for avatar
  const userName = Context?.state?.user?.attributes?.name || "";
  const userPicture = Context?.state?.user?.attributes?.picture || "";

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (localStorage.getItem("sponsorCodePreFill")) {
      let sponsorCodeFromLocalStorage = localStorage.getItem("sponsorCodePreFill");
      setSponsorCode(sponsorCodeFromLocalStorage);
      getEventDetailsBySponsorCode(sponsorCodeFromLocalStorage)
        .then(async (res) => {
          let eventDetails = JSON.parse(res.data.getEventDetailsBySponsorCode);
          if (eventDetails.statusCode === 200) {
            try {
              await sendEventDetailsToApi(
                eventDetails.body[0].sponsorCode,
                eventDetails.body[0].eventId
              );
              listEvents().then(
                res => {
                  let allEventDetails = JSON.parse(res.data.listEventsForSponsor);
                  if (allEventDetails.statusCode == 200) {
                    let allEvents = allEventDetails.body;
                    addAllEventsToIndexDB(allEvents);
                    setAddedEventDatails(eventDetails.body[0]);
                    setAddedEventModal(true);
                  }
                }
              ).catch(err => console.log(err));
            } catch (err) {
              console.log(err);
            }
          } else {
            setAddedEventModal(false);
            setSponsorCode(sponsorCodeFromLocalStorage);
          }
        })
        .catch((err) => console.log(err));
      localStorage.removeItem("sponsorCodePreFill");
    }
  }, []);

  const submitClickHandler = async () => {
    setLoading(true);
    try {
      const res = await getEventDetailsBySponsorCode(sponsorCode);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in getEventDetailsBySponsorCode:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to get event details";
        showError(errorMessage);
        setLoading(false);
        return;
      }

      let eventDetails = JSON.parse(res.data.getEventDetailsBySponsorCode);
      if (eventDetails.statusCode == 200) {
        sendEventDetailsToApi(
          eventDetails.body[0].sponsorCode,
          eventDetails.body[0].eventId
        );
        setLoading(false);
        setError("");
        navigate("/events");
      } else {
        setLoading(false);
        setError(JSON.parse(eventDetails.body));
      }
    } catch (err) {
      console.error("Error in getEventDetailsBySponsorCode:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to get event details";
      showError(errorMessage);
      setLoading(false);
    }
  };

  const sendEventDetailsToApi = async (sponsorCode, eventId) => {
    setLoading(true);
    const profileUserId = Context.state.user.attributes["custom:userId"];
    let event_details = {
      userId: profileUserId,
      eventId: eventId,
      sponsorCode: sponsorCode,
    };
    try {
      const res = await createUserSponsorMapping(event_details);

      // Check for GraphQL errors in response
      if (res.errors && res.errors.length > 0) {
        console.error("GraphQL errors in createUserSponsorMapping:", res.errors);
        const errorMessage = res.errors[0]?.message || "Failed to add event";
        showError(errorMessage);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error("Error in createUserSponsorMapping:", err);
      const errorMessage = err?.errors?.[0]?.message || err?.message || "Failed to add event";
      showError(errorMessage);
      setLoading(false);
    }
  };

  const sponsorEventClickHandler = () => {
    navigate({
      pathname: `/events/${addedEventDatails.eventId}/scanDetails`,
      search: `?sponsor=${addedEventDatails.sponsorCode}`,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && sponsorCode) {
      submitClickHandler();
    }
  };

  // Form content shared between mobile and desktop - render function (not a component)
  const renderFormContent = () => (
    <>
      <p className="sponsor-id-page__description">
        Enter Sponsor ID to access the event for which you have been added as sponsor.
      </p>
      <div>
        <label className="sponsor-id-page__label">Sponsor ID</label>
        <input
          type="text"
          className={`sponsor-id-page__input ${error ? "sponsor-id-page__input--error" : ""}`}
          placeholder="Enter Sponsor ID"
          value={sponsorCode}
          onChange={(e) => {
            setSponsorCode(e.target.value);
            setError("");
          }}
          onKeyDown={handleKeyDown}
          maxLength={6}
          name="sponsor-id-field"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
          inputMode="text"
          enterKeyHint="go"
          data-1p-ignore="true"
          data-lpignore="true"
          data-form-type="other"
        />
        {error && <span className="sponsor-id-page__error">{error}</span>}
      </div>
      <button
        className="sponsor-id-page__btn"
        onClick={submitClickHandler}
        disabled={!sponsorCode || loading}
      >
        {loading ? <span className="app-spinner"></span> : "Let's Go"}
      </button>
    </>
  );

  // Mobile Header
  const renderMobileHeader = () => (
    <header className="app-header">
      <div className="app-header__logo">Sponsor App</div>
      <div className="app-header__actions">
        <button
          className="app-header__theme-btn"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          <Icon
            icon={theme === "dark" ? "flowbite:sun-outline" : "flowbite:moon-outline"}
            width="24"
            height="24"
          />
        </button>
        <Avatar
          src={userPicture}
          name={userName}
          size={36}
          onClick={() => setProfileModalOpen(true)}
        />
      </div>
    </header>
  );

  // Desktop Layout
  if (isDesktop) {
    return (
      <div className="sponsor-id-page">
        <AppHeader onProfileClick={() => setProfileModalOpen(true)} />
        <main className="sponsor-id-page__content">
          <div className="sponsor-id-page__container">
            <h1 className="sponsor-id-page__title">Sponsor App</h1>
            <div className="sponsor-id-page__card">
              {renderFormContent()}
            </div>
          </div>
        </main>
        <AddedEventDisplayModal
          addedEventModal={addedEventModal}
          setAddedEventModal={setAddedEventModal}
          closeButton={false}
          accessEventClickHandler={sponsorEventClickHandler}
          addedEventDatails={addedEventDatails}
        />
        <ProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
        />
      </div>
    );
  }

  // Mobile Layout
  return (
    <div className="sponsor-id-page sponsor-id-page--mobile">
      {renderMobileHeader()}
      <main className="sponsor-id-mobile">
        <h1 className="sponsor-id-mobile__title">Sponsor App</h1>
        <div className="sponsor-id-mobile__card">
          {renderFormContent()}
        </div>
      </main>
      <AddedEventDisplayModal
        addedEventModal={addedEventModal}
        setAddedEventModal={setAddedEventModal}
        closeButton={false}
        accessEventClickHandler={sponsorEventClickHandler}
        addedEventDatails={addedEventDatails}
      />
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />
    </div>
  );
};

export default NoEventSponsor;
